import { useEffect, useState } from 'react';
// MIGRATION: fully on AWS PostgREST.
import { aws } from '../lib/awsdb';
import { authWrite } from '../lib/authWrite';
import '../po/drawer.css';

const CATEGORIES = ['Supplier', 'Agency', 'Freelancer', 'Partner', 'Service Provider'];

// Title-case helper — capitalise first letter of every word so person
// fields stay tidy regardless of how Mo (or staff) types. Same regex
// the ContactAutocomplete uses internally.
function autoCap(s) {
  return (s || '').replace(/\b\w/g, c => c.toUpperCase());
}

function formatWhen(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
}

function logContactActivity(contactId, session, payload) {
  return aws.from('contact_activity_log').insert([{
    contact_id: contactId, actor_code: session.code, actor_name: session.name,
    action_type: payload.action_type, notes: payload.notes ?? null
  }]);
}

export default function ContactDrawer({ session, contact, onClose, onSaved }) {
  const isEdit = !!contact?.id;
  const [tab, setTab] = useState(isEdit ? 'activity' : 'details');
  const [form, setForm] = useState(() => contact ? { ...contact, tags: (contact.tags || []).join(', ') } : {
    company_name: '', contact_person: '', email: '', phone: '', category: 'Supplier',
    tags: '', street: '', city: '', state: '', postcode: '', country: 'Australia',
    abn: '', bank_account_name: '', bank_bsb: '', bank_account_number: '', bank_swift: '',
    notes: '', status: 'active'
  });
  const [activity, setActivity] = useState([]);
  const [saving, setSaving] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [posting, setPosting] = useState(false);
  // Memories — notes/facts/preferences about this person, surfaced
  // anywhere the contact gets pulled into context (cases, chat, etc).
  // Migration 100 added contact_memories.
  const [memories, setMemories] = useState([]);
  const [memText, setMemText] = useState('');
  const [memKind, setMemKind] = useState('note');
  const [memSaving, setMemSaving] = useState(false);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!isEdit) return;
    loadAllActivity(contact);
    loadMemories(contact);
  }, [contact, isEdit]);

  async function loadMemories(c) {
    if (!c?.id) return;
    const { data } = await aws.from('contact_memories')
      .select('*').eq('contact_id', c.id)
      .order('created_at', { ascending: false }).limit(100);
    setMemories(data || []);
  }

  async function addMemory() {
    const body = memText.trim();
    if (!body) return;
    setMemSaving(true);
    try {
      await authWrite(session, 'contact_memories', 'insert', {
        contact_id: contact.id,
        kind: memKind,
        content: body,
        created_by: session?.code || null,
      });
      setMemText('');
      await loadMemories(contact);
    } catch (e) {
      alert('Could not save memory: ' + (e.message || e));
    } finally {
      setMemSaving(false);
    }
  }

  async function deleteMemory(id) {
    if (!confirm('Delete this memory?')) return;
    try {
      await authWrite(session, 'contact_memories', 'delete', null, { column: 'id', value: id });
      setMemories(m => m.filter(x => x.id !== id));
    } catch (e) {
      alert('Could not delete: ' + (e.message || e));
    }
  }

  async function loadAllActivity(c) {
    if (!c?.company_name) return;
    const name = c.company_name;
    // general_invoices + general_invoice_activity_log were dropped in
    // Supabase migration 27 — do not include them.
    const results = await Promise.all([
      aws.from('contact_activity_log').select('*').eq('contact_id', c.id).order('created_at', { ascending: false }),
      aws.from('invoice_activity_log').select('*, po_invoices!inner(supplier_name)').ilike('po_invoices.supplier_name', name),
      aws.from('po_activity_log').select('*, purchase_orders!inner(supplier_name)').ilike('purchase_orders.supplier_name', name),
    ]);
    const all = [
      ...(results[0].data || []).map(a => ({ ...a, _src: 'contact' })),
      ...(results[1].data || []).map(a => ({ id: a.id, actor_name: a.actor_name, action_type: a.action_type, after_value: a.after_value, before_value: a.before_value, notes: a.notes, created_at: a.created_at, _src: 'po_invoice', _ref: a.po_invoices?.invoice_number })),
      ...(results[2].data || []).map(a => ({ id: a.id, actor_name: a.actor_name, action_type: a.action || a.action_type, after_value: a.detail, notes: null, created_at: a.created_at, _src: 'po', _ref: a.purchase_orders?.po_number })),
    ].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    setActivity(all);
  }

  async function save() {
    const personTrim = (form.contact_person || '').trim();
    const companyTrim = (form.company_name || '').trim();
    if (!personTrim && !companyTrim) {
      alert('Enter a person name, a company name, or both.'); return;
    }
    setSaving(true);
    const row = {
      company_name: companyTrim || null, contact_person: personTrim || null,
      email: form.email || null, phone: form.phone || null, category: form.category || 'Supplier',
      tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      street: form.street || null, city: form.city || null, state: form.state || null,
      postcode: form.postcode || null, country: form.country || null,
      abn: form.abn || null, bank_account_name: form.bank_account_name || null,
      bank_bsb: form.bank_bsb || null, bank_account_number: form.bank_account_number || null,
      bank_swift: form.bank_swift || null, notes: form.notes || null, status: form.status || 'active'
    };
    if (isEdit) {
      await authWrite(session, 'contacts', 'update', row, { column: 'id', value: contact.id });
      await logContactActivity(contact.id, session, { action_type: 'contact_edited' });
    } else {
      row.created_by = session.code;
      const inserted = await authWrite(session, 'contacts', 'insert', row);
      const created = Array.isArray(inserted) ? inserted[0] : null;
      if (created) await logContactActivity(created.id, session, { action_type: 'contact_created' });
    }
    setSaving(false); onSaved?.(); onClose();
  }

  async function postComment() {
    if (!commentText.trim() || !contact?.id) return;
    setPosting(true);
    await logContactActivity(contact.id, session, { action_type: 'comment', notes: commentText.trim() });
    setCommentText('');
    setPosting(false);
    loadAllActivity(contact);
  }

  function mask(val) { if (!val) return '—'; return val.length > 4 ? '••••' + val.slice(-4) : val; }

  return (
    <>
      <div className="drawer-overlay" onClick={onClose} />
      <aside className="drawer">
        <div className="drawer-header">
          <div className="drawer-title">{isEdit ? (form.contact_person || form.company_name) : 'Add Contact'}</div>
          <button className="drawer-close" onClick={onClose}>×</button>
        </div>
        {isEdit && (
          <div className="po-drawer-tabs">
            <button className={`po-drawer-tab ${tab === 'activity' ? 'active' : ''}`} onClick={() => setTab('activity')}>Activity</button>
            <button className={`po-drawer-tab ${tab === 'memories' ? 'active' : ''}`} onClick={() => setTab('memories')}>Memories{memories.length > 0 && <span style={{ marginLeft: 6, opacity: 0.6, fontSize: 11 }}>{memories.length}</span>}</button>
            <button className={`po-drawer-tab ${tab === 'details' ? 'active' : ''}`} onClick={() => setTab('details')}>Details</button>
          </div>
        )}
        <div className="drawer-content">
          {(tab === 'details' || !isEdit) && (<>
            <div className="form-group"><label className="form-label">Person Name</label>
              <input className="form-input" value={form.contact_person || ''} onChange={e => setField('contact_person', autoCap(e.target.value))} placeholder="e.g. Andrew" /></div>
            <div className="form-group"><label className="form-label">Company Name</label>
              <input className="form-input" value={form.company_name || ''} onChange={e => setField('company_name', e.target.value)} placeholder="e.g. Phyto-Therapy Pty Ltd" /></div>
            <div style={{ fontSize: 11, color: 'var(--text-subdued)', marginTop: -8, marginBottom: 8 }}>Fill in either, or both. At least one is required.</div>
            <div className="form-group"><label className="form-label">Category</label>
              <select className="form-select" value={form.category || ''} onChange={e => setField('category', e.target.value)}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select></div>
            <div className="row-2">
              <div className="form-group"><label className="form-label">Email</label><input type="email" className="form-input" value={form.email || ''} onChange={e => setField('email', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Phone</label><input className="form-input" value={form.phone || ''} onChange={e => setField('phone', e.target.value)} /></div>
            </div>
            <div className="form-group"><label className="form-label">Tags <span className="field-hint">comma-separated</span></label>
              <input className="form-input" value={form.tags || ''} onChange={e => setField('tags', e.target.value)} placeholder="Meta Ads, Packaging, Freight" /></div>
            <div className="form-group"><label className="form-label">ABN / Tax ID</label><input className="form-input" value={form.abn || ''} onChange={e => setField('abn', e.target.value)} /></div>

            <div className="subsection">
              <div className="subsection-label">Address</div>
              <div className="subsection-body">
                <div className="form-group"><input className="form-input" value={form.street || ''} onChange={e => setField('street', e.target.value)} placeholder="Street" /></div>
                <div className="row-2">
                  <div className="form-group"><input className="form-input" value={form.city || ''} onChange={e => setField('city', e.target.value)} placeholder="City" /></div>
                  <div className="form-group"><input className="form-input" value={form.state || ''} onChange={e => setField('state', e.target.value)} placeholder="State" /></div>
                </div>
                <div className="row-2">
                  <div className="form-group"><input className="form-input" value={form.postcode || ''} onChange={e => setField('postcode', e.target.value)} placeholder="Postcode" /></div>
                  <div className="form-group"><input className="form-input" value={form.country || ''} onChange={e => setField('country', e.target.value)} placeholder="Country" /></div>
                </div>
              </div>
            </div>

            <div className="subsection">
              <div className="subsection-label">Bank Details</div>
              <div className="subsection-body">
                <div className="form-group"><input className="form-input" value={form.bank_account_name || ''} onChange={e => setField('bank_account_name', e.target.value)} placeholder="Account Name" /></div>
                <div className="row-2">
                  <div className="form-group"><input className="form-input" value={form.bank_bsb || ''} onChange={e => setField('bank_bsb', e.target.value)} placeholder="BSB" /></div>
                  <div className="form-group"><input className="form-input" value={form.bank_account_number || ''} onChange={e => setField('bank_account_number', e.target.value)} placeholder="Account Number" /></div>
                </div>
                <div className="form-group"><input className="form-input" value={form.bank_swift || ''} onChange={e => setField('bank_swift', e.target.value)} placeholder="SWIFT (optional)" /></div>
              </div>
            </div>

            <div className="row-2">
              <div className="form-group"><label className="form-label">Status</label>
                <select className="form-select" value={form.status || 'active'} onChange={e => setField('status', e.target.value)}>
                  <option value="active">Active</option><option value="inactive">Inactive</option>
                </select></div>
              <div className="form-group"><label className="form-label">Notes</label>
                <textarea className="form-textarea" value={form.notes || ''} onChange={e => setField('notes', e.target.value)} placeholder="Internal notes..." /></div>
            </div>

            {isEdit && contact.created_at && (
              <div className="muted-small" style={{ marginTop: 12 }}>Created {formatWhen(contact.created_at)}{contact.created_by ? ` by ${contact.created_by}` : ''}</div>
            )}
          </>)}

          {tab === 'memories' && isEdit && (
            <div className="po-comments-tab">
              <div className="po-comment-input" style={{ alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
                    {['note', 'fact', 'preference'].map(k => (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setMemKind(k)}
                        style={{
                          fontSize: 11, padding: '4px 10px', borderRadius: 999,
                          border: '1px solid ' + (memKind === k ? '#0F1419' : '#E2DDD2'),
                          background: memKind === k ? '#0F1419' : 'transparent',
                          color: memKind === k ? '#F4EFE6' : '#52575C',
                          cursor: 'pointer', textTransform: 'capitalize',
                          fontFamily: 'inherit',
                        }}
                      >{k}</button>
                    ))}
                  </div>
                  <textarea
                    className="form-textarea"
                    placeholder={memKind === 'preference'
                      ? 'e.g. prefers WhatsApp to email; only takes calls before 10am AEDT'
                      : memKind === 'fact'
                      ? 'e.g. previously worked at Pacific Caps before founding Eastern'
                      : 'A note about this person…'}
                    value={memText}
                    onChange={e => setMemText(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) addMemory(); }}
                  />
                </div>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={memSaving || !memText.trim()}
                  onClick={addMemory}
                >{memSaving ? 'Saving…' : 'Add'}</button>
              </div>
              <div className="po-unified-feed">
                {memories.length === 0 && (
                  <div className="po-card-activity-empty">
                    No memories yet. Add notes, facts, or preferences so future
                    cases get smarter about this person.
                  </div>
                )}
                {memories.map(m => (
                  <div key={m.id} className="po-feed-comment" style={{ position: 'relative' }}>
                    <div className="po-feed-comment-header">
                      <span className="po-feed-comment-author" style={{ textTransform: 'capitalize' }}>
                        {m.kind}{m.created_by ? ` · ${m.created_by}` : ''}
                      </span>
                      <span className="po-feed-comment-when">{formatWhen(m.created_at)}</span>
                    </div>
                    <div className="po-feed-comment-body" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{m.content}</div>
                    <button
                      type="button"
                      onClick={() => deleteMemory(m.id)}
                      title="Delete memory"
                      style={{
                        position: 'absolute', top: 8, right: 8,
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        color: '#A8ADB1', fontSize: 16, lineHeight: 1, padding: '2px 6px',
                      }}
                    >×</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'activity' && isEdit && (
            <div className="po-comments-tab">
              <div className="po-comment-input">
                <textarea className="form-textarea" placeholder="Add a comment..." value={commentText} onChange={e => setCommentText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) postComment(); }} />
                <button className="btn btn-primary btn-sm" disabled={posting || !commentText.trim()} onClick={postComment}>{posting ? 'Posting...' : 'Post'}</button>
              </div>
              <div className="po-unified-feed">
                {activity.length === 0 && <div className="po-card-activity-empty">No activity yet</div>}
                {activity.map(entry => entry.action_type === 'comment' ? (
                  <div key={entry.id} className="po-feed-comment">
                    <div className="po-feed-comment-header">
                      <span className="po-feed-comment-author">{entry.actor_name || 'Someone'}</span>
                      <span className="po-feed-comment-when">{formatWhen(entry.created_at)}</span>
                    </div>
                    <div className="po-feed-comment-body">{entry.notes}</div>
                  </div>
                ) : (
                  <div key={entry.id} className="po-feed-activity">
                    <span className="po-feed-activity-who">{entry.actor_name || 'System'}</span>
                    <span className="po-feed-sep">·</span>
                    <span className="po-feed-activity-what">
                      {entry.action_type?.replace(/_/g, ' ')}
                      {entry._ref && <> · <strong>{entry._src === 'po' ? (entry._ref.match(/^PO/i) ? entry._ref : `PO ${entry._ref}`) : `INV ${entry._ref}`}</strong></>}
                      {entry.after_value && <> · {entry.after_value}</>}
                    </span>
                    <span className="po-feed-sep">·</span>
                    <span className="po-feed-activity-when">{formatWhen(entry.created_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {(tab === 'details' || !isEdit) && (
          <div className="drawer-footer">
            <button className="btn btn-secondary btn-full" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary btn-full" disabled={saving} onClick={save}>{saving ? 'Saving...' : 'Save'}</button>
          </div>
        )}
      </aside>
    </>
  );
}
